import { star } from "@/figmaElements";
import { useAccelerateLabel } from "@/hooks/useAccelerateLabel";
import { cn } from "@/lib/utils";
import { DetailedPlayerAction } from "@/pages/DetailedPlayer/reducer";
import {
  chemstyleBonusesSelector,
  contentLabelsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import {
  ChemStyleAccelerateGroup,
  getChemStyleAccelerate,
  groupChemStylesByAccelerate,
} from "@repo/shared/utils";
import { useEffect } from "react";
import { useSelector } from "react-redux";

type Props = {
  chemistry: number;
  height: number;
  inGamePlayer: { [label: string]: number };
  bestChemStyle: number;
  selectedChemStyleId: number;
  hoveredChemStyleId: number;
  handleAction: (action: DetailedPlayerAction) => void;
  isGk: boolean;
  setAccelerate: (accelerate: string) => void;
  gender: "MALE" | "FEMALE";
};

const ChemstyleButton = ({
  id,
  handleAction,
  selected,
  best,
}: {
  id: number;
  handleAction: (action: DetailedPlayerAction) => void;
  selected: boolean;
  best: boolean;
}) => {
  const { chemstyles } = useSelector(contentLabelsSelector);
  return (
    <button
      className={cn(
        "flex flex-row justify-start gap-x-2 w-32 px-2 h-9 mobile:h-7 items-center bg-action-tertiary rounded-md shrink-0 text-dark-secondary",
        selected
          ? "bg-action-tertiary-active text-dark-primary"
          : "hover:bg-action-tertiary-hover hover:text-dark-primary",
      )}
      onClick={() => handleAction({ type: "SET_CHEMSTYLE", payload: id })}
      onMouseEnter={() =>
        handleAction({ type: "SET_TEMPORARY_CHEMSTYLE", payload: id })
      }
    >
      <div
        className="text-xl"
        style={{
          fontFamily: "UltimateTeamIcons",
        }}
      >
        {chemStylesToIcon[id]}
      </div>
      <p className="text-xs">{chemstyles[id]?.name}</p>
      {best && <div className="w-3 h-3 ml-auto">{star}</div>}
    </button>
  );
};

export const ChemStyles = ({
  chemistry,
  selectedChemStyleId,
  hoveredChemStyleId,
  height,
  inGamePlayer,
  bestChemStyle,
  handleAction,
  isGk,
  setAccelerate,
  gender,
}: Props) => {
  const chemstyleBonuses = useSelector(chemstyleBonusesSelector);
  const accelerateLabel = useAccelerateLabel();
  useEffect(() => {
    setAccelerate(
      getChemStyleAccelerate({
        chemStyleId: hoveredChemStyleId || selectedChemStyleId,
        chemistry,
        inGameAttributes: inGamePlayer,
        height,
        gender,
        chemstyleBonuses,
      }),
    );
  }, [
    hoveredChemStyleId,
    selectedChemStyleId,
    chemistry,
    inGamePlayer,
    height,
    chemstyleBonuses,
  ]);

  const accelerateGroups = groupChemStylesByAccelerate({
    isGk,
    chemistry,
    inGameAttributes: inGamePlayer,
    height,
    gender,
    chemstyleBonuses,
  });
  const groupButtons = (styles: ChemStyleAccelerateGroup["chemStyles"]) =>
    styles.map(({ id }) => (
      <ChemstyleButton
        best={id === bestChemStyle}
        key={id}
        id={id}
        handleAction={handleAction}
        selected={id === selectedChemStyleId}
      />
    ));

  const chemStylesSection = (
    label: string,
    chemStyles: any,
    handleAction: (action: DetailedPlayerAction) => void,
  ) => (
    <div
      key={label}
      className="flex flex-col"
      style={{
        width: `${Math.ceil(chemStyles.length / 4) * 132}px`,
      }}
    >
      <div className="text-xs text-dark-tertiary">{label}</div>
      <div
        className="flex flex-wrap gap-1"
        onMouseLeave={() =>
          handleAction({ type: "SET_TEMPORARY_CHEMSTYLE", payload: undefined })
        }
      >
        {chemStyles}
      </div>
    </div>
  );

  return (
    <div className="flex flex-row gap-x-2 mobile:gap-x-4">
      {accelerateGroups.map((group) =>
        chemStylesSection(
          accelerateLabel(group.accelerate),
          groupButtons(group.chemStyles),
          handleAction,
        ),
      )}
    </div>
  );
};
